// Single source of truth for the reservation **status taxonomy** in the web app:
// which statuses count as what, how each maps to a label/color/icon, the
// arrival-time thresholds, and the small per-table / overlap helpers that used
// to be hand-rolled inline ~20 times across the Tables / VisualTables /
// ReservationsSchedule pages (and had drifted apart).
//
// Status *groups* live in `@lemmonhq/types` (CONFIRMED_STATUSES etc.) because
// they're contract-level; the UI mapping (Tailwind classes, translation keys,
// icons) and the arrival thresholds live here because they're web-specific.
//
// Mirrors the existing REQUEST_STATUS_CONFIG pattern used for reservation
// *requests* (ReservationTabs/config.ts).
import {
  type Reservation,
  type ReservationStatus,
  CONFIRMED_STATUSES,
  SEATED_STATUSES,
  ACTIVE_TABLE_STATUSES,
  CANCELLED_STATUSES,
} from "@lemmonhq/types";

// ── Status predicates ──────────────────────────────────────────────────────
// Accept a plain string so callers don't need to cast `r.status`.
const inGroup = (group: readonly string[], status: string) =>
  group.includes(status);

/** Booked, nobody seated yet ("changed" = edited-but-still-confirmed). */
export const isConfirmedStatus = (s: string) => inGroup(CONFIRMED_STATUSES, s);
/** The party is physically at the table (seated / arrived / partially-seated). */
export const isSeatedStatus = (s: string) => inGroup(SEATED_STATUSES, s);
/** Occupies/holds a table — used for floor occupancy, counts and conflicts. */
export const isActiveTableStatus = (s: string) =>
  inGroup(ACTIVE_TABLE_STATUSES, s);
/** No longer holds a table (no-show / cancelled). */
export const isCancelledStatus = (s: string) => inGroup(CANCELLED_STATUSES, s);

// ── Status category ────────────────────────────────────────────────────────
export type ReservationCategory =
  | "upcoming"
  | "seated"
  | "completed"
  | "cancelled";

export function categorizeReservation(status: string): ReservationCategory {
  if (isSeatedStatus(status)) return "seated";
  if (status === "completed") return "completed";
  if (isCancelledStatus(status)) return "cancelled";
  return "upcoming"; // confirmed / changed (and any unknown → treated as booked)
}

// ── Tone → literal Tailwind classes ────────────────────────────────────────
// Tailwind's JIT only emits classes it can see as literals, so every class used
// anywhere must appear verbatim here. Call sites pick the slot they need
// (badge / legend / bar / icon) rather than rebuilding `bg-${tone}` strings.
export type StatusTone = "success" | "info" | "error" | "warning" | "neutral";

export const TONE_CLASSES: Record<
  StatusTone,
  {
    badgeBg: string;
    badgeText: string;
    legendBg: string;
    barClass: string;
    borderClass: string;
  }
> = {
  success: {
    badgeBg: "bg-success/15",
    badgeText: "text-success",
    legendBg: "bg-success",
    barClass: "bg-success/70",
    borderClass: "border-success",
  },
  info: {
    badgeBg: "bg-info/15",
    badgeText: "text-info",
    legendBg: "bg-info",
    barClass: "bg-info/70",
    borderClass: "border-info",
  },
  error: {
    badgeBg: "bg-error/15",
    badgeText: "text-error",
    legendBg: "bg-error",
    barClass: "bg-error/60",
    borderClass: "border-error",
  },
  warning: {
    badgeBg: "bg-warning/15",
    badgeText: "text-warning",
    legendBg: "bg-warning",
    barClass: "bg-warning/70",
    borderClass: "border-warning",
  },
  neutral: {
    badgeBg: "bg-base-content/10",
    badgeText: "text-base-content/60",
    legendBg: "bg-base-content/40",
    barClass: "bg-base-content/30",
    borderClass: "border-base-content/30",
  },
};

// ── Per-category style (label / tone / icon) ───────────────────────────────
export const RESERVATION_STATUS_STYLE: Record<
  ReservationCategory,
  { labelKey: string; tone: StatusTone; icon: string }
> = {
  upcoming: {
    labelKey: "reservations.status.confirmed",
    tone: "success",
    icon: "event_available",
  },
  seated: {
    labelKey: "reservations.table_popup.seated_tab",
    tone: "info",
    icon: "restaurant",
  },
  completed: {
    labelKey: "reservations.status.completed",
    tone: "neutral",
    icon: "task_alt",
  },
  cancelled: {
    labelKey: "reservations.status.cancelled",
    tone: "error",
    icon: "cancel",
  },
};

// ── Arrival-time thresholds (one window everywhere) ────────────────────────
// Unified per product decision: a booking reads "soon" from 15 min before its
// time, and "late" once 30 min past — on the floor ribbon AND the sidebar list,
// so the same booking never reads two different urgencies at once.
export const ARRIVING_SOON_MIN = 15;
export const LATE_AFTER_MIN = 30;

export type ArrivalState = "upcoming" | "soon" | "late";

/** @param timeIso reservation time (UTC ISO) @param nowMs epoch ms (e.g. useNow) */
export function getArrivalState(timeIso: string, nowMs: number): ArrivalState {
  const diffMins = (Date.parse(timeIso) - nowMs) / 60000;
  if (diffMins <= -LATE_AFTER_MIN) return "late";
  if (diffMins <= ARRIVING_SOON_MIN) return "soon";
  return "upcoming";
}

/** Tone + translation key for an arrival urgency badge (list + floor agree). */
export const ARRIVAL_STYLE: Record<
  Exclude<ArrivalState, "upcoming">,
  { labelKey: string; tone: StatusTone }
> = {
  soon: { labelKey: "reservations.floor.arriving_soon", tone: "warning" },
  late: { labelKey: "reservations.floor.late", tone: "error" },
};

// ── Small shared utilities ─────────────────────────────────────────────────
/** Sort comparator by reservation start time (parses once per comparison). */
export const byStartTime = (a: { time: string }, b: { time: string }) =>
  Date.parse(a.time) - Date.parse(b.time);

/** Do time windows [aStart,aEnd) and [bStart,bEnd) overlap? (epoch ms). */
export const overlaps = (
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
) => aStart < bEnd && aEnd > bStart;

/** Reservations assigned to `tableId` whose status is in `statuses`, time-sorted. */
export function reservationsForTable(
  reservations: Reservation[],
  tableId: number,
  statuses: readonly ReservationStatus[] | readonly string[],
): Reservation[] {
  return reservations
    .filter(
      (r) =>
        (statuses as readonly string[]).includes(r.status) &&
        r.table_assignments?.some(
          (a) => Number(a.table_id) === Number(tableId),
        ),
    )
    .sort(byStartTime);
}

/** "HH:mm" in the restaurant timezone (24h) — keeps the floor ribbon compact. */
export const formatTimeHHmm = (timeIso: string | Date, tz: string) =>
  new Date(timeIso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: tz,
  });

/** "HH:mm-HH:mm" start–end range in the restaurant timezone (24h). */
export const formatTimeRangeHHmm = (
  timeIso: string,
  durationMins: number,
  tz: string,
) => {
  const start = new Date(timeIso);
  const end = new Date(start.getTime() + durationMins * 60000);
  return `${formatTimeHHmm(start, tz)}-${formatTimeHHmm(end, tz)}`;
};
